/*
  (c) 2026 Fluxonix Inc.
*/

import { useState, useRef, useEffect } from "react";

export default function CapturePage() {
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState("");
  const [images, setImages] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [audioBase64, setAudioBase64] = useState("");
  const [micStatus, setMicStatus] = useState("idle");
  // idle | listening | no_signal | noise_only | speech_detected | blocked
  const [audioReviewed, setAudioReviewed] = useState(false);

  const [micMessage, setMicMessage] = useState("");
  const [audioHealth, setAudioHealth] = useState({
    maxRms: 0,
    speechFrames: 0,
    noiseFrames: 0,
    silentFrames: 0,
  });

  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const micStreamRef = useRef(null);
  const monitorIntervalRef = useRef(null);

  const workflowOptions = [
    "Design",
    "Incoming Material Inspection",
    "Supplier Quality Containment",
    "Production Quality Escalation",
    "Packaging / Shipping Damage",
    "Equipment / Downtime Incident",
    "Other",
  ];

  const workflowGuidanceChecklist = {
    "Design": [
      "Part, product, or system affected",
      "Design change or concern",
      "Reason for the change",
      "Engineering question or decision needed",
      "Photos, drawings, or review evidence attached",
    ],

    "Incoming Material Inspection": [
      "Supplier name",
      "Material / part number",
      "PO, lot, batch, or heat number",
      "Quantity received and quantity affected",
      "Defect or mismatch observed",
      "Disposition: accept, hold, reject, or escalate",
    ],

    "Supplier Quality Containment": [
      "Supplier name",
      "Affected material or part",
      "Defect, risk, or non-conformance",
      "Quantity impacted",
      "Containment action taken",
      "Next owner or follow-up needed",
    ],

    "Production Quality Escalation": [
      "Production line or workstation",
      "Part, batch, or job affected",
      "Issue observed",
      "Quantity impacted",
      "Immediate action taken",
      "Who was notified",
    ],

    "Packaging / Shipping Damage": [
      "Shipment, PO, or delivery reference",
      "Packaging condition",
      "Items damaged",
      "Quantity affected",
      "Carrier, supplier, or customer if known",
      "Photos attached",
    ],

    "Equipment / Downtime Incident": [
      "Machine or equipment name",
      "Location or production line",
      "Failure symptom",
      "Downtime start time",
      "Production impact",
      "Maintenance action needed",
    ],

    "Other": [
      "What happened",
      "Where it happened",
      "Who or what was involved",
      "Why it matters",
      "Action needed next",
    ],
  };

  const severityOptions = [
    "Low",
    "Medium",
    "High",
    "Containment Required",
  ];

  const evidenceTypeOptions = [
    "Design Review",
    "Material Label",
    "Heat Number Tag",
    "Packaging Damage",
    "Incoming Inspection",
    "Production Defect",
    "Supplier Documentation",
    "Equipment Condition",
    "Other",
  ];

  const [workflowType, setWorkflowType] = useState(
    "Design"
  );
  const [customWorkflowType, setCustomWorkflowType] = useState("");

  const [severity, setSeverity] = useState(
    "Review Required"
  );

  const [evidenceType, setEvidenceType] = useState(
    "Design Review"
  );

  const DB_NAME = "fluxonix_capture_db";

  const DB_VERSION = 1;

  const AUDIO_DRAFT_STORE = "audio_drafts";

  const LATEST_AUDIO_DRAFT_ID = "latest_audio_draft";

  const [customEvidenceType, setCustomEvidenceType] = useState("");

  const [artifacts, setArtifacts] = useState([]);
  const [selectedArtifactId, setSelectedArtifactId] = useState(null);

  useEffect(() => {
    const hydratePage = async () => {
      try {
        const savedArtifacts = localStorage.getItem("fluxonix_artifacts");

        if (savedArtifacts) {
          const parsedArtifacts = JSON.parse(savedArtifacts);

          if (Array.isArray(parsedArtifacts) && parsedArtifacts.length > 0) {
            setArtifacts(parsedArtifacts);
            setSelectedArtifactId(parsedArtifacts[0].id);
          }
        }
      } catch (err) {
        console.error("Failed to load saved artifacts from localStorage", err);
      }

      try {
        const draft = await loadAudioDraft();

        if (draft?.blob) {
          setAudioBlob(draft.blob);
          setAudioUrl(URL.createObjectURL(draft.blob));
          setAudioBase64("");
        }
      } catch (err) {
        console.error("Failed to load audio draft from IndexedDB", err);
      }
    };

    hydratePage();
  }, []);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const API_BASE = "/public";
  const pathParts = window.location.pathname.split("/");
  const capturedBy = pathParts[2] || "unknown";

  const openCaptureDb = () => {

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(AUDIO_DRAFT_STORE)) {
          db.createObjectStore(AUDIO_DRAFT_STORE, { keyPath: "id" });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  };

  const saveAudioDraft = async (blob) => {
    const db = await openCaptureDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(AUDIO_DRAFT_STORE, "readwrite");
      const store = tx.objectStore(AUDIO_DRAFT_STORE);
      store.put({
        id: LATEST_AUDIO_DRAFT_ID,
        blob,
        mimeType: blob.type || "audio/webm",
        createdAt: new Date().toISOString(),
      });
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
  };

  const loadAudioDraft = async () => {
    const db = await openCaptureDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(AUDIO_DRAFT_STORE, "readonly");
      const store = tx.objectStore(AUDIO_DRAFT_STORE);
      const request = store.get(LATEST_AUDIO_DRAFT_ID);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  };

  const clearAudioDraft = async () => {
    const db = await openCaptureDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(AUDIO_DRAFT_STORE, "readwrite");
      const store = tx.objectStore(AUDIO_DRAFT_STORE);
      store.delete(LATEST_AUDIO_DRAFT_ID);
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
  };

  const getFileExtension = (mimeType) => {
    if (mimeType.includes("webm")) return "webm";
    if (mimeType.includes("mp4")) return "mp4";
    if (mimeType.includes("m4a")) return "m4a";
    return "webm";
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.readAsDataURL(file);

      reader.onload = () => resolve(reader.result);

      reader.onerror = (error) => reject(error);
    });
  };

  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.readAsDataURL(blob);

      reader.onloadend = () => resolve(reader.result);

      reader.onerror = reject;
    });
  };

  const startAudioHealthMonitor = (stream) => {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(stream);

    analyser.fftSize = 2048;
    source.connect(analyser);

    audioContextRef.current = audioContext;
    analyserRef.current = analyser;

    const dataArray = new Uint8Array(analyser.fftSize);

    let maxRms = 0;
    let speechFrames = 0;
    let noiseFrames = 0;
    let silentFrames = 0;
    let elapsedFrames = 0;

    monitorIntervalRef.current = setInterval(() => {
      analyser.getByteTimeDomainData(dataArray);

      let sumSquares = 0;

      for (let i = 0; i < dataArray.length; i++) {
        const normalized = (dataArray[i] - 128) / 128;
        sumSquares += normalized * normalized;
      }

      const rms = Math.sqrt(sumSquares / dataArray.length);
      maxRms = Math.max(maxRms, rms);
      elapsedFrames += 1;

      // Tune these after real shop-floor tests
      const SILENCE_THRESHOLD = 0.008;
      const SPEECH_THRESHOLD = 0.035;

      if (rms < SILENCE_THRESHOLD) {
        silentFrames += 1;
      } else if (rms >= SPEECH_THRESHOLD) {
        speechFrames += 1;
      } else {
        noiseFrames += 1;
      }

      setAudioHealth({
        maxRms,
        speechFrames,
        noiseFrames,
        silentFrames,
      });

      if (elapsedFrames > 20 && maxRms < SILENCE_THRESHOLD) {
        setMicStatus("no_signal");
        setMicMessage("We can’t hear you. Check your microphone or unmute.");
        return;
      }

      if (elapsedFrames > 35 && speechFrames < 4 && noiseFrames > 10) {
        setMicStatus("noise_only");
        setMicMessage("We hear background noise, but no clear speech. Move closer to the mic.");
        return;
      }

      if (speechFrames >= 4) {
        setMicStatus("speech_detected");
        setMicMessage("Voice detected. Keep recording clearly.");
        return;
      }

      setMicStatus("listening");
      setMicMessage("Listening for your voice...");
    }, 100);
  };

  const stopAudioHealthMonitor = () => {
    if (monitorIntervalRef.current) {
      clearInterval(monitorIntervalRef.current);
      monitorIntervalRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    analyserRef.current = null;

    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
    }
  };

  const startRecording = async () => {

    setAudioReviewed(false);  
    setMicStatus("listening");
    setMicMessage("Listening for your voice...");
    setAudioHealth({
      maxRms: 0,
      speechFrames: 0,
      noiseFrames: 0,
      silentFrames: 0,
    });

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    micStreamRef.current = stream;
    startAudioHealthMonitor(stream);

    let mimeType = "";

    if (MediaRecorder.isTypeSupported("audio/webm")) {
      mimeType = "audio/webm";
    } else if (MediaRecorder.isTypeSupported("audio/mp4")) {
      mimeType = "audio/mp4";
    } else if (MediaRecorder.isTypeSupported("audio/m4a")) {
      mimeType = "audio/m4a";
    }

    const mediaRecorder = mimeType
      ? new MediaRecorder(stream, { mimeType })
      : new MediaRecorder(stream);

    mediaRecorderRef.current = mediaRecorder;
    chunksRef.current = [];

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunksRef.current.push(e.data);
      }
    };

    mediaRecorder.onstop = async () => {
      const finalType = mediaRecorder.mimeType || mimeType || "audio/webm";
      const blob = new Blob(chunksRef.current, { type: finalType });

      const localAudioUrl = URL.createObjectURL(blob);

      setAudioBlob(blob);
      setAudioUrl(localAudioUrl);
      setAudioBase64("");

      try {
        await saveAudioDraft(blob);
        console.log("Audio draft saved to IndexedDB");
      } catch (err) {
        console.error("Failed to save audio draft to IndexedDB", err);
        alert("Recording was created, but could not be saved locally.");
      }
    };

    mediaRecorder.start();
    setRecording(true);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }

    stopAudioHealthMonitor();
    setAudioReviewed(false);
    setRecording(false);

    if (audioHealth.speechFrames < 4) {
      setMicStatus("blocked");
      setMicMessage("This recording does not appear to contain usable speech. Please re-record.");
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);

    const processedImages = await Promise.all(
      files.map(async (file) => {
        const base64 = await convertToBase64(file);

        return {
          file,
          name: file.name,
          previewUrl: URL.createObjectURL(file),
          base64,
        };
      })
    );

    setImages((prev) => [...prev, ...processedImages]);

    setImagePreviews((prev) => [
      ...prev,
      ...processedImages,
    ]);
  };

  const createDemoTimeline = () => {
    const now = new Date();

    return [
      {
        label: "Voice observation captured",
        time: now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        detail: "Frontline signal recorded directly from the point of work.",
      },
      {
        label: "Evidence attached",
        time: now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        detail:
          images.length > 0
            ? `${images.length} photo${images.length === 1 ? "" : "s"} linked to this artifact.`
            : "Voice-only operational capture recorded. No photo evidence attached.",
      },
      {
        label: "Incident reconstructed",
        time: now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        detail: "Structured decision artifact generated from frontline inputs.",
      },
      {
        label: "Risk signals identified",
        time: now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        detail: "Operational risks, open questions, and next actions extracted.",
      },
    ];
  };

    const exportArtifact = (artifact) => {
    
    const audioSection = artifact.audioBase64
    ? `
      <audio controls style="width:100%; margin-top:12px;">
        <source src="${artifact.audioBase64}" type="${artifact.audioMimeType}">
        Your browser does not support audio playback.
      </audio>
    `
    : "<p>No audio evidence attached.</p>";

    const imageSection =
      artifact.evidenceImages?.length > 0
        ? artifact.evidenceImages
            .map(
              (img) => `
                <div style="margin-bottom:16px;">
                  <img 
                    src="${img.base64}" 
                    alt="${img.name}" 
                    style="
                      max-width:100%;
                      max-height:500px;
                      object-fit:contain;
                      border-radius:8px;
                      border:1px solid #ddd;
                    "
                  />
                  <div style="font-size:12px;color:#666;margin-top:4px;">
                    ${img.name}
                  </div>
                </div>
              `
            )
            .join("")
        : "<p>No evidence images attached.</p>";

    const timelineSection = artifact.timeline
      .map(
        (item) => `
          <div style="margin-bottom:18px;">
            <div style="font-weight:600;">${item.label}</div>
            <div style="font-size:12px;color:#666;">${item.time}</div>
            <div style="margin-top:4px;">${item.detail}</div>
          </div>
        `
      )
      .join("");

    const decisionSignals = artifact.decisionSignals
      .map((signal) => `<li style="margin-bottom:8px;">${signal}</li>`)
      .join("");

    const html = `
      <html>
        <head>
        <meta charset="UTF-8" />
          <title>${artifact.title}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 40px;
              line-height: 1.6;
              color: #111;
            }

            h1, h2, h3 {
              color: #1d4ed8;
            }

            .section {
              margin-top: 40px;
            }

            .card {
              border: 1px solid #ddd;
              border-radius: 12px;
              padding: 20px;
              background: #fafafa;
            }
          </style>
        </head>

        <body>
          <h1>${artifact.title}</h1>

          <div>
            <strong>Artifact ID:</strong> ${artifact.id}<br />
            <strong>Captured:</strong> ${artifact.capturedAt}<br />
            <strong>Captured By:</strong> ${artifact.capturedBy}<br />
            <strong>Workflow Type:</strong> ${artifact.workflowType}<br />
            <strong>Evidence Type:</strong> ${artifact.evidenceType}<br />
            <strong>Severity:</strong> ${artifact.riskLevel}<br />
            <strong>Status:</strong> ${artifact.status}<br />            
          </div>

          <div class="section">
            <h2>Incident Timeline</h2>
            <div class="card">
              ${timelineSection}
            </div>
          </div>

          <div class="section">
            <h2>Decision Signals</h2>
            <div class="card">
              <ul>
                ${decisionSignals}
              </ul>
            </div>
          </div>

          <div class="section">
            <h2>Operational Evidence</h2>
            <div class="card">
              ${audioSection}
            </div>
          </div>

          <div class="section">
            <h2>Photo Evidence</h2>
            <div class="card">
              ${imageSection}
            </div>
          </div>

          <div class="section">
            <h2>Decision Artifact</h2>
            <div class="card">
              ${artifact.html}
            </div>
          </div>
        </body>
      </html>
    `;

    const blob = new Blob([html], { type: "text/html" });

    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${artifact.id}.html`;

    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportNcrDocument = (artifact) => {
  const issueDate = new Date().toLocaleDateString();

  const imageSection =
    artifact.evidenceImages?.length > 0
      ? artifact.evidenceImages
          .map(
            (img) => `
              <div style="margin-bottom:16px;">
                <img
                  src="${img.base64}"
                  alt="${img.name}"
                  style="
                    max-width:100%;
                    max-height:420px;
                    object-fit:contain;
                    border:1px solid #ccc;
                    border-radius:6px;
                  "
                />
                <div style="font-size:11px;color:#666;margin-top:4px;">
                  ${img.name}
                </div>
              </div>
            `
          )
          .join("")
      : "<p>No image evidence attached.</p>";

  const html = `
  <html>
    <head>
      <meta charset="UTF-8" />

      <title>NCR-${artifact.id}</title>

      <style>
        body {
          font-family: Arial, sans-serif;
          padding: 40px;
          color: #111;
          line-height: 1.5;
        }

        h1 {
          margin-bottom: 6px;
          color: #b91c1c;
        }

        h2 {
          margin-top: 36px;
          margin-bottom: 12px;
          padding-bottom: 6px;
          border-bottom: 1px solid #ddd;
          color: #1f2937;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 12px;
        }

        td, th {
          border: 1px solid #ccc;
          padding: 10px;
          vertical-align: top;
          font-size: 14px;
        }

        .label {
          width: 220px;
          background: #f3f4f6;
          font-weight: bold;
        }

        .badge {
          display: inline-block;
          padding: 6px 10px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: bold;
        }

        .high {
          background: #fee2e2;
          color: #991b1b;
        }

        .medium {
          background: #fef3c7;
          color: #92400e;
        }

        .low {
          background: #dcfce7;
          color: #166534;
        }

        .section-box {
          border: 1px solid #ddd;
          border-radius: 8px;
          padding: 16px;
          margin-top: 12px;
          background: #fafafa;
        }

        .signature-box {
          height: 80px;
        }

        .footer {
          margin-top: 50px;
          font-size: 12px;
          color: #666;
        }
      </style>
    </head>

    <body>

      <h1>NON-CONFORMANCE REPORT (NCR)</h1>

      <div style="margin-bottom:24px;">
        Generated by Fluxonix Operational Memory Layer
      </div>

      <table>
        <tr>
          <td class="label">NCR ID</td>
          <td>NCR-${artifact.id}</td>
        </tr>

        <tr>
          <td class="label">Date</td>
          <td>${issueDate}</td>
        </tr>

        <tr>
          <td class="label">Captured By</td>
          <td>${artifact.capturedBy}</td>
        </tr>

        <tr>
          <td class="label">Workflow Type</td>
          <td>${artifact.workflowType}</td>
        </tr>

        <tr>
          <td class="label">Evidence Type</td>
          <td>${artifact.evidenceType}</td>
        </tr>

        <tr>
          <td class="label">Severity</td>
          <td>
            <span class="badge ${
              artifact.riskLevel === "High" ||
              artifact.riskLevel === "Containment Required"
                ? "high"
                : artifact.riskLevel === "Medium"
                ? "medium"
                : "low"
            }">
              ${artifact.riskLevel}
            </span>
          </td>
        </tr>
      </table>

      <h2>Description of Discrepancy</h2>

      <div class="section-box">
        ${
          artifact.html ||
          "Operational event captured from frontline workflow."
        }
      </div>

      <h2>Containment Actions</h2>

      <div class="section-box">
        <ul>
          <li>Material/process flagged for review.</li>
          <li>Operational evidence linked to NCR packet.</li>
          <li>Further disposition pending quality review.</li>
        </ul>
      </div>

      <h2>Risk Signals</h2>

      <div class="section-box">
        <ul>
          ${artifact.decisionSignals
            .map((signal) => `<li>${signal}</li>`)
            .join("")}
        </ul>
      </div>

      <h2>Evidence Package</h2>

      <div class="section-box">
        ${imageSection}
      </div>

      <h2>Disposition</h2>

      <table>
        <tr>
          <td>Use As-Is</td>
          <td></td>
        </tr>

        <tr>
          <td>Rework</td>
          <td></td>
        </tr>

        <tr>
          <td>Scrap</td>
          <td></td>
        </tr>

        <tr>
          <td>Return to Supplier</td>
          <td></td>
        </tr>
      </table>

      <h2>Approvals</h2>

      <table>
        <tr>
          <th>Role</th>
          <th>Name</th>
          <th>Signature</th>
          <th>Date</th>
        </tr>

        <tr class="signature-box">
          <td>Quality Engineer</td>
          <td></td>
          <td></td>
          <td></td>
        </tr>

        <tr class="signature-box">
          <td>Operations Manager</td>
          <td></td>
          <td></td>
          <td></td>
        </tr>

        <tr class="signature-box">
          <td>Supplier Representative</td>
          <td></td>
          <td></td>
          <td></td>
        </tr>
      </table>

      <div class="footer">
        Fluxonix generated this NCR draft from frontline operational evidence,
        voice capture, and attached contextual signals.
      </div>

    </body>
  </html>
  `;

  const blob = new Blob([html], { type: "text/html" });

  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");

  a.href = url;
  a.download = `NCR-${artifact.id}.html`;

  document.body.appendChild(a);

  a.click();

  document.body.removeChild(a);

  URL.revokeObjectURL(url);
};

  const handleSubmit = async () => {
    if (!audioBlob || !email) {
      alert("Please record audio and enter email.");
      return;
    }

    setLoading(true);

    // ADD VALIDATION HERE
    if (audioHealth.speechFrames < 4 || audioHealth.maxRms < 0.015) {
      alert("This recording does not appear to contain usable speech. Please re-record before creating an artifact.");
      setLoading(false);
      return;
    }

    if (!audioReviewed) {
      alert("Please play back the recording and confirm your voice is clear before submitting.");
      setLoading(false);
      return;
    }

    if (workflowType === "Other" && !customWorkflowType.trim()) {
      alert("Please enter a workflow type.");
      setLoading(false);
      return;
    }

    if (evidenceType === "Other" && !customEvidenceType.trim()) {
      alert("Please enter an evidence type.");
      setLoading(false);
      return;
    }

    const extension = getFileExtension(audioBlob.type);
    const file = new File([audioBlob], `audio.${extension}`, {
      type: audioBlob.type,
    });

    const formData = new FormData();
    formData.append("audio", file);
    images.forEach((img) => formData.append("images", img.file));
    formData.append("email", email);
    formData.append("captured_by", capturedBy);

    try {
      const res = await fetch(`${API_BASE}/api/capture`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      const finalWorkflowType =
        workflowType === "Other"
          ? customWorkflowType.trim() || "Other"
          : workflowType;

      const finalEvidenceType =
        evidenceType === "Other"
          ? customEvidenceType.trim() || "Other"
          : evidenceType;

      const artifact = {
        id: data.submission_id || `FX-${Date.now()}`,
        title: "Operational Event Captured",
        capturedAt: new Date().toLocaleString(),
        capturedBy,
        workflowType: finalWorkflowType,
        evidenceType: finalEvidenceType,
        html: data.html,
        audioUrl,
        imageCount: images.length,
        evidenceImages: imagePreviews,
        severity,
        audioBase64: "",
        audioMimeType: audioBlob?.type || "audio/webm",
        status: "Decision Artifact Created",
        riskLevel: severity,
        evidenceType,
        timeline: createDemoTimeline(),
        decisionSignals: [
          "Operational context captured before system-of-record entry",

          images.length > 0
            ? "Evidence package generated from voice and photo inputs"
            : "Evidence package generated from frontline voice input",

          images.length > 0
            ? "Photo evidence reviewed for operational context"
            : "No photo evidence attached during capture",

          "Open questions and risk markers available for review",
        ],
      };

      setArtifacts((prev) => {
        const updatedArtifacts = [artifact, ...prev];

        const artifactsForStorage = updatedArtifacts.map((item) => ({
          ...item,
          audioBase64: "",
          audioUrl: "",
          evidenceImages: item.evidenceImages?.map((img) => ({
            name: img.name,
            previewUrl: img.previewUrl,
          })),
        }));

        try {
          localStorage.setItem(
            "fluxonix_artifacts",
            JSON.stringify(artifactsForStorage)
          );
        } catch (err) {
          console.error("Failed to save artifacts to localStorage", err);
        }

        return updatedArtifacts;
      });

      setSelectedArtifactId(artifact.id);

      setAudioBlob(null);
      setAudioUrl("");
      setImages([]);
      //images.forEach((img) => URL.revokeObjectURL(img.previewUrl));
      setImagePreviews([]);
      //await clearAudioDraft();
    } catch (err) {
      console.error(err);
      alert("Unable to create decision artifact.");
    } finally {
      setLoading(false);
    }
  };

  const selectedArtifact =
    artifacts.find((artifact) => artifact.id === selectedArtifactId) || artifacts[0];

  return (
    <div className="min-h-screen bg-gray-50 px-6 py-8">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 lg:grid-cols-[420px_1fr]">
        {/* Left Capture Panel */}
        <div className="rounded-2xl bg-white p-8 shadow-lg">
          <h1 className="mb-2 text-2xl font-semibold">Capture What Happened</h1>
          <p className="mb-8 text-sm text-gray-500">
            Create a decision artifact from voice, photos, and frontline context.
          </p>

          <div className="mb-8 flex flex-col items-center">
            <button
              onClick={recording ? stopRecording : startRecording}
              className={`flex h-24 w-24 items-center justify-center rounded-full text-xl text-white shadow-lg ${
                recording ? "bg-red-500" : "bg-blue-600"
              }`}
            >
              🎤
            </button>

            <p className="mt-3 text-sm text-gray-600">
              {recording ? "Recording..." : audioBlob ? "Recording ready" : "Tap to record"}
            </p>

            {audioUrl && (
              <audio controls src={audioUrl} className="mt-4 w-full" />
            )}
            {micMessage && (
              <div
                className={`mt-3 rounded-lg px-4 py-3 text-sm ${
                  micStatus === "speech_detected"
                    ? "border border-green-200 bg-green-50 text-green-800"
                    : micStatus === "no_signal" ||
                      micStatus === "noise_only" ||
                      micStatus === "blocked"
                    ? "border border-red-200 bg-red-50 text-red-800"
                    : "border border-blue-200 bg-blue-50 text-blue-800"
                }`}
              >
                {micMessage}
              </div>
            )}
            {audioUrl && (
              <div className="mt-4 w-full rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="mb-2 text-sm text-amber-900">
                  Play Recording
                </p>

                <label className="flex items-center gap-2 text-sm text-amber-900">
                  <input
                    type="checkbox"
                    checked={audioReviewed}
                    onChange={(e) => setAudioReviewed(e.target.checked)}
                  />
                  I reviewed the recording and can clearly hear my voice.
                </label>
              </div>
            )}
          </div>

          <div className="mb-6">
            <label className="mb-2 block text-sm text-gray-600">
              Add evidence photos
            </label>
            <input
              type="file"
              multiple
              onChange={handleImageUpload}
              className="block w-full text-sm"
            />
            {images.length > 0 && (
              <p className="mt-2 text-xs text-gray-500">
                {images.length} photo{images.length === 1 ? "" : "s"} attached
              </p>
            )}
          </div>

        <div className="mb-6">
            <label className="mb-2 block text-sm text-gray-600">
              Workflow Type
            </label>
            <select
              value={workflowType}
              onChange={(e) => setWorkflowType(e.target.value)}
              className="w-full rounded-lg border px-3 py-2 text-sm"
            >
              {workflowOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {workflowType === "Other" && (
              <input
                type="text"
                placeholder="Enter workflow type"
                value={customWorkflowType}
                onChange={(e) => setCustomWorkflowType(e.target.value)}
                className="mt-3 w-full rounded-lg border px-3 py-2 text-sm"
              />
            )}
            <div className="mt-3 rounded-lg border border-blue-100 bg-blue-50 px-3 py-3">
              <div className="mb-2 text-xs font-semibold text-blue-900">
                What to mention in your recording
              </div>

              <ul className="space-y-1 text-xs text-blue-900">
                {(workflowGuidanceChecklist[workflowType] ||
                  workflowGuidanceChecklist.Other
                ).map((item) => (
                  <li key={item} className="flex gap-2">
                    <span>✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
        </div>

          <div className="mb-6">
          <label className="mb-2 block text-sm text-gray-600">
            Severity Classification
          </label>

          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm"
          >
            {severityOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-6">
          <label className="mb-2 block text-sm text-gray-600">
            Evidence Type
          </label>

          <select
            value={evidenceType}
            onChange={(e) => setEvidenceType(e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm"
          >
            {evidenceTypeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>

          {evidenceType === "Other" && (
            <input
              type="text"
              placeholder="Enter evidence type"
              value={customEvidenceType}
              onChange={(e) => setCustomEvidenceType(e.target.value)}
              className="mt-3 w-full rounded-lg border px-3 py-2 text-sm"
            />
          )}
        </div>

          <div className="mb-6">
            <label className="mb-2 block text-sm text-gray-600">
              Send artifact to
            </label>
            <input
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border px-3 py-2 text-sm"
            />
          </div>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full rounded-lg bg-blue-600 px-6 py-3 text-white shadow-md hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400"
          >
            {loading ? "Creating Artifact..." : "Create Operational Artifact"}
          </button>
        </div>

        {/* Right Operational Memory Panel */}
        <div className="rounded-2xl bg-white shadow-lg">
          <div className="border-b p-6">
            <h2 className="text-xl font-semibold">Operational Memory</h2>
            <p className="mt-1 text-sm text-gray-500">
              Decision artifacts created from frontline events.
            </p>
          </div>

          {artifacts.length === 0 ? (
            <div className="p-10 text-center text-sm text-gray-500">
              No decision artifacts yet. Record an event to start building the operational timeline.
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr]">
              {/* Artifact Ledger */}
              <div className="border-r p-4">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Decision Artifact Ledger
                </h3>

                <div className="space-y-3">
                  {artifacts.map((artifact) => (
                    <button
                      key={artifact.id}
                      onClick={() => setSelectedArtifactId(artifact.id)}
                      className={`w-full rounded-xl border p-4 text-left transition ${
                        selectedArtifact?.id === artifact.id
                          ? "border-blue-600 bg-blue-50"
                          : "border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <div className="text-sm font-semibold">{artifact.title}</div>
                      <div className="mt-1 text-xs text-gray-500">{artifact.capturedAt}</div>
                      <div className="mt-3 flex flex-wrap gap-2">
                      <span className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-700">
                        {artifact.imageCount} photos
                      </span>

                      <span className="rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-800">
                        {artifact.workflowType}
                      </span>

                      <span className="rounded-full bg-purple-100 px-2 py-1 text-xs text-purple-800">
                        {artifact.evidenceType}
                      </span>

                      <span
                        className={`rounded-full px-2 py-1 text-xs ${
                          artifact.riskLevel === "Low"
                            ? "bg-green-100 text-green-800"
                            : artifact.riskLevel === "Medium"
                            ? "bg-yellow-100 text-yellow-800"
                            : artifact.riskLevel === "High"
                            ? "bg-orange-100 text-orange-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {artifact.riskLevel}
                      </span>
                    </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Expanded Artifact */}
              {selectedArtifact && (
                <div className="p-6">
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold">{selectedArtifact.title}</h3>
                    <p className="text-sm text-gray-500">
                      {selectedArtifact.id} • {selectedArtifact.capturedAt}
                    </p>
                  </div>

                  <div className="mb-6 flex gap-3">
                  <button
                    onClick={() => exportArtifact(selectedArtifact)}
                    className="rounded-lg bg-gray-900 px-4 py-2 text-sm text-white hover:bg-black"
                  >
                    Export Artifact
                  </button>
                  <button
                    onClick={() => exportNcrDocument(selectedArtifact)}
                    className="rounded-lg bg-red-700 px-4 py-2 text-sm text-white hover:bg-red-800"
                  >
                    Generate NCR Draft
                  </button>
                </div>

                  {/* Incident Timeline */}
                  <section className="mb-8">
                    <h4 className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-500">
                      Incident Timeline
                    </h4>

                    <div className="space-y-4">
                      {selectedArtifact.timeline.map((item, index) => (
                        <div key={index} className="flex gap-4">
                          <div className="flex flex-col items-center">
                            <div className="h-3 w-3 rounded-full bg-blue-600" />
                            {index !== selectedArtifact.timeline.length - 1 && (
                              <div className="mt-1 h-full w-px bg-gray-200" />
                            )}
                          </div>

                          <div className="pb-4">
                            <div className="text-sm font-semibold">{item.label}</div>
                            <div className="text-xs text-gray-500">{item.time}</div>
                            <div className="mt-1 text-sm text-gray-700">{item.detail}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>

                  {/* Evidence */}
                  <section className="mb-8 rounded-xl border bg-gray-50 p-4">
                    <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
                      Evidence
                    </h4>

                    {selectedArtifact.audioUrl ? (
                      <div className="mb-4">
                        <p className="mb-2 text-sm font-medium">Original Voice Capture</p>
                        <audio controls src={selectedArtifact.audioUrl} className="w-full" />
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No audio link available.</p>
                    )}

                    <div>
                    <p className="mb-3 text-sm font-medium">
                      Photo Evidence ({selectedArtifact.imageCount})
                    </p>

                    {selectedArtifact.evidenceImages?.length > 0 ? (
                      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                        {selectedArtifact.evidenceImages.map((img, index) => (
                          <div
                            key={index}
                            className="overflow-hidden rounded-lg border bg-white"
                          >
                            <img
                              src={img.previewUrl}
                              alt={img.name}
                              className="h-40 w-full object-cover"
                            />
                            <div className="truncate px-2 py-1 text-xs text-gray-500">
                              {img.name}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">
                        No photo evidence attached.
                      </p>
                    )}
                  </div>
                  </section>

                  {/* Decision Signals */}
                  <section className="mb-8">
                    <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
                      Decision Signals
                    </h4>

                    <ul className="space-y-2">
                      {selectedArtifact.decisionSignals.map((signal, index) => (
                        <li
                          key={index}
                          className="rounded-lg border border-gray-200 bg-white p-3 text-sm"
                        >
                          {signal}
                        </li>
                      ))}
                    </ul>
                  </section>

                  {/* Original Artifact Preview */}
                  <section>
                    <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
                      Decision Artifact
                    </h4>

                    <div className="rounded-xl border bg-white p-5">
                      <div
                        className="prose max-w-none text-sm [&_h3]:mt-6 [&_h3]:text-blue-600"
                        dangerouslySetInnerHTML={{ __html: selectedArtifact.html }}
                      />
                    </div>
                  </section>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}